import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-[#1c3144] text-white hover:bg-[#2a4558]",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline: "border border-[#d9d9e0] bg-white hover:bg-[#f9f9fb] text-[#1c2024]",
        secondary: "bg-[#f9f9fb] text-[#1c2024] hover:bg-[#ececf1]",
        ghost: "hover:bg-[#f9f9fb] text-[#1c2024]",
        link: "text-[#4c6f90] underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const sizeStyles = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-[60px] px-6 text-base",
  icon: "h-10 w-10",
  "icon-sm": "h-8 w-8",
  "icon-lg": "h-12 w-12",
} as const;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    size?: keyof typeof sizeStyles;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size = "md", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant }),
          className,
          sizeStyles[size]
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
